import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  limit,
  orderBy,
  startAfter,
  getCountFromServer,
  DocumentSnapshot,
} from '@angular/fire/firestore';

@Injectable({ providedIn: 'root' })
export class FirebaseTableService {

  constructor(private firestore: Firestore) {}
  
  // Get total count of documents in a collection
  async getTotalCount(collectionName: string): Promise<number> {
    const colRef = collection(this.firestore, collectionName);
    const snapshot = await getCountFromServer(colRef);
    return snapshot.data().count;
  }

  // Get paginated data with server-side pagination
  async getPaginatedData(
    collectionName: string, 
    pageSize: number = 10, 
    lastDoc?: DocumentSnapshot,
    queryConfig?: { field: string; op: any; param: any },
    sortField: string = 'createdAt',
    sortDirection: 'asc' | 'desc' = 'desc'
  ): Promise<{ data: any[], lastDoc: DocumentSnapshot | null, hasMore: boolean }> {
    const colRef = collection(this.firestore, collectionName);
    let constraints: any[] = [];

    // Apply query filter if provided
    if (queryConfig) {
      constraints.push(where(queryConfig.field, queryConfig.op, queryConfig.param));
    }

    // Apply sorting
    constraints.push(orderBy(sortField, sortDirection));

    // Apply pagination cursor
    if (lastDoc) {
      constraints.push(startAfter(lastDoc));
    }

    // Always apply limit
    constraints.push(limit(pageSize));

    // Build the query using all constraints
    const q = query(colRef, ...constraints);
    
    const snapshot = await getDocs(q);
    const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Get the last document for next page
    const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
    
    // Check if there are more documents by trying to get one more
    let hasMore = false;
    if (newLastDoc && data.length === pageSize) {
      const nextConstraints = [...constraints.slice(0, -1), startAfter(newLastDoc), limit(1)];
      const nextQuery = query(colRef, ...nextConstraints);
      const nextSnapshot = await getDocs(nextQuery);
      hasMore = nextSnapshot.docs.length > 0;
    }
    
    return { data, lastDoc: newLastDoc, hasMore };
  }

  async getAll(name: string, rowLimit?: number): Promise<any[]> {
    let q = collection(this.firestore, name);
    
    // Apply limit if specified
    if (rowLimit && rowLimit > 0) {
      const limitedQuery = query(q, limit(rowLimit));
      const snapshot = await getDocs(limitedQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } else {
      // Get all documents if no limit specified
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    }
  }

  async getDoc(collectionName: string, docId: string): Promise<any | null> {
    const docRef = doc(this.firestore, collectionName, docId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docId, ...docSnap.data() };
    }
    return null;
  }

  async add(name: string, data: any): Promise<void> {
    const colRef = collection(this.firestore, name);
    await addDoc(colRef, data);
  }

  async update(name: string, id: string, data: any): Promise<void> {
    const docRef = doc(this.firestore, name, id);
    await updateDoc(docRef, data);
  }

  async delete(name: string, id: string): Promise<void> {
    const docRef = doc(this.firestore, name, id);
    await deleteDoc(docRef);
  }

  async deleteBatch(name: string, ids: string[]): Promise<void> {
    for (const id of ids) {
      const docRef = doc(this.firestore, name, id);
      await deleteDoc(docRef);
    }
  }

  async deleteAll(name: string): Promise<void> {
    const colRef = collection(this.firestore, name);
    const snapshot = await getDocs(colRef);
    for (const docSnap of snapshot.docs) {
      await deleteDoc(doc(this.firestore, name, docSnap.id));
    }
  }
}
